var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isInIframe, sendToHost } from "../iframe/postMessageActions";
/**
 * Transport layer for Tapcart App Proxy operations
 * (`tapcart-mobile-api/api/routes/proxy.js`).
 *
 * Dual dispatch based on environment:
 *   - **Top-level browser** (no iframe): direct `fetch` against the
 *     relative App Proxy URL. Same-origin to the merchant storefront,
 *     so Shopify's App Proxy signing layer wraps it transparently.
 *   - **Cross-origin iframe** (production, served by Theme App Extension):
 *     postMessage to the parent's `host-bridge.js`, which performs the
 *     fetch on the merchant origin. Required because the iframe lives
 *     on Tapcart's origin and can't reach `/apps/<prefix>/*` directly.
 *
 * The dispatch is invisible to callers — `wishlist.ts` action dispatchers
 * call these functions without knowing which path runs.
 *
 * For both paths the App Proxy backend `req.appProxy.customerId`
 * provides the verified Shopify identity (via signed query params in the
 * top-level case, via the cookied same-origin fetch in the iframe case).
 * Guests get 401 `GUEST_CUSTOMER`, surfaced here as `isGuestError(err)`.
 */
const DEFAULT_APP_PROXY_BASE = "/apps/tapcart";
/**
 * Top-level fetch timeout. Matches the iframe-mode 10s `sendToHost`
 * timeout so the two paths fail in the same shape. Without this, a slow
 * or hung App Proxy keeps every storefront wishlist call open until the
 * browser eventually cuts the socket — visible to users as a frozen UI.
 */
const REQUEST_TIMEOUT_MS = 10000;
const fetchWithTimeout = (url, init) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    return fetch(url, Object.assign(Object.assign({}, init), { signal: controller.signal })).finally(() => clearTimeout(timeoutId));
};
const appProxyError = (status, code, message) => {
    const err = new Error(message);
    err.status = status;
    err.code = code;
    err.isGuest = code === "GUEST_CUSTOMER" || status === 401;
    return err;
};
const buildUrl = (config, path) => {
    var _a;
    const base = ((_a = config.appProxyBase) !== null && _a !== void 0 ? _a : DEFAULT_APP_PROXY_BASE).replace(/\/$/, "");
    const sep = path.includes("?") ? "&" : "?";
    return `${base}${path}${sep}appId=${encodeURIComponent(config.appId)}`;
};
const handleFetchResponse = (res) => __awaiter(void 0, void 0, void 0, function* () {
    if (res.ok) {
        return (yield res.json());
    }
    let body = null;
    try {
        body = yield res.json();
    }
    catch (_a) {
        /* response was not JSON */
    }
    throw appProxyError(res.status, body === null || body === void 0 ? void 0 : body.code, (body === null || body === void 0 ? void 0 : body.message) || res.statusText || `HTTP ${res.status}`);
});
/**
 * Wrap a sendToHost call as a Promise that resolves on success and
 * rejects with an `AppProxyError` on host-side error (preserving the
 * `code` field so `isGuestError` can detect `GUEST_CUSTOMER`).
 */
const dispatchViaHost = (actionType, payload) => {
    return new Promise((resolve, reject) => {
        sendToHost(actionType, payload, {
            onSuccess: (data) => resolve(data),
            onError: (err) => {
                var _a, _b;
                // host-bridge.js error payload shape: { status, code, message }
                if (err && typeof err === "object") {
                    const e = err;
                    reject(appProxyError((_a = e.status) !== null && _a !== void 0 ? _a : 500, e.code, (_b = e.message) !== null && _b !== void 0 ? _b : "host bridge error"));
                }
                else {
                    reject(appProxyError(500, undefined, err instanceof Error ? err.message : "host bridge error"));
                }
            },
        });
    });
};
export const isGuestError = (err) => !!(err && typeof err === "object" && err.isGuest);
// Exported for the wire-shape contract test (tests/normalize.test.mjs),
// which locks the wishlist-service → bridge mapping against captured real
// service responses. This boundary is the one that broke in production
// (service returns `{data:[{_id}]}`, bridge expected `{wishlists:[{id}]}`).
export const normalizeWishlist = (raw) => {
    var _a, _b, _c;
    // Guard against non-array payloads: the wishlist service has been seen to
    // return `items: null` (and a malformed/unexpected shape would blow up
    // `.map`). Prefer `items`, fall back to `variants`, else empty — only ever
    // mapping an actual array.
    const rawItems = Array.isArray(raw === null || raw === void 0 ? void 0 : raw.items)
        ? raw.items
        : Array.isArray(raw === null || raw === void 0 ? void 0 : raw.variants)
            ? raw.variants
            : [];
    return {
        id: String((_b = (_a = raw === null || raw === void 0 ? void 0 : raw.id) !== null && _a !== void 0 ? _a : raw === null || raw === void 0 ? void 0 : raw._id) !== null && _b !== void 0 ? _b : ""),
        name: (_c = raw === null || raw === void 0 ? void 0 : raw.name) !== null && _c !== void 0 ? _c : "Wishlist",
        items: rawItems.map((item) => {
            var _a, _b;
            return ({
                id: (_a = item === null || item === void 0 ? void 0 : item.id) !== null && _a !== void 0 ? _a : item === null || item === void 0 ? void 0 : item._id,
                productId: (_b = item === null || item === void 0 ? void 0 : item.productId) !== null && _b !== void 0 ? _b : "",
                variantId: item === null || item === void 0 ? void 0 : item.variantId,
                collectionId: item === null || item === void 0 ? void 0 : item.collectionId,
                brandId: item === null || item === void 0 ? void 0 : item.brandId,
            });
        }),
    };
};
export const extractWishlistArray = (raw) => {
    if (Array.isArray(raw))
        return raw;
    const obj = (raw !== null && raw !== void 0 ? raw : {});
    if (Array.isArray(obj.wishlists))
        return obj.wishlists;
    if (Array.isArray(obj.data))
        return obj.data;
    return [];
};
// ───────────────────────────────────────────────────────────────────────────
// Wishlist operations
// ───────────────────────────────────────────────────────────────────────────
export const appProxyListWishlists = (config) => __awaiter(void 0, void 0, void 0, function* () {
    const raw = isInIframe()
        ? yield dispatchViaHost("wishlists/get", { appId: config.appId })
        : yield handleFetchResponse(yield fetchWithTimeout(buildUrl(config, "/wishlist"), {
            credentials: "include",
        }));
    return { wishlists: extractWishlistArray(raw).map(normalizeWishlist) };
});
export const appProxyCreateWishlist = (config, 
// Optional: when omitted (the default-list bootstrap), the wishlist service
// resolves the merchant's configured default name so web converges with
// mobile instead of hardcoding "My Favorites". An explicit name (e.g. a
// user-created multi-wishlist) is forwarded unchanged.
name) => __awaiter(void 0, void 0, void 0, function* () {
    const payload = name === undefined ? {} : { name };
    const raw = isInIframe()
        ? yield dispatchViaHost("wishlist/create", Object.assign({ appId: config.appId }, payload))
        : yield handleFetchResponse(yield fetchWithTimeout(buildUrl(config, "/wishlist"), {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }));
    return normalizeWishlist(raw);
});
export const appProxyAddItem = (config, payload) => __awaiter(void 0, void 0, void 0, function* () {
    const raw = isInIframe()
        ? yield dispatchViaHost("wishlist/item/add", Object.assign({ appId: config.appId }, payload))
        : yield handleFetchResponse(yield fetchWithTimeout(buildUrl(config, "/wishlist/item"), {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }));
    return normalizeWishlist(raw);
});
export const appProxyRemoveItem = (config, payload) => __awaiter(void 0, void 0, void 0, function* () {
    const raw = isInIframe()
        ? yield dispatchViaHost("wishlist/item/remove", Object.assign({ appId: config.appId }, payload))
        : yield handleFetchResponse(yield fetchWithTimeout(buildUrl(config, "/wishlist/item"), {
            method: "DELETE",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }));
    return normalizeWishlist(raw);
});
